import type { IFolderScanner } from "@/application/ports/services/IFolderScanner";
import type { IZipExtractor } from "@/application/ports/services/IZipExtractor";
import type { ISignedUrlUploader } from "@/application/ports/services/ISignedUrlUploader";
import { uploadFileToSignedUrl } from "@/infrastructure/api/gcsUpload";
import { filterFolderFileList, scanEntries } from "@/infrastructure/services/folderScanner";
import { extractZip } from "@/infrastructure/services/zipExtractor";
import type { IProjectSpreadsheetService } from "@/application/ports/services/IProjectSpreadsheetService";
import { ProjectSpreadsheetService } from "@/infrastructure/services/ProjectSpreadsheetService";
import type { ILogger } from "@/application/ports/ILogger";
import type { IMonitoring } from "@/application/ports/IMonitoring";
import type { INavigationService } from "@/application/ports/INavigationService";
import type {
  IAuthenticationService,
  IStorageService,
  ITokenService,
  IUserStorageService,
} from "@/application/ports/services";
import { EventNotifier } from "@/application/services/EventNotifier";
import { TokenManager } from "@/application/services/TokenManager";
import { AuthenticationService } from "@/infrastructure/auth/AuthenticationService";
import { logger } from "@/infrastructure/logger";
import {
  forceNavigate,
  openExternalUrl,
  reloadPage,
  getPageOrigin,
} from "@/infrastructure/navigation";
import {
  captureException,
  captureMessage,
  clearUserContext,
  setUserContext,
} from "@/infrastructure/monitoring";
import { FileDownloadService } from "@/infrastructure/services/FileDownloadService";
import { AppSettingsStorageService } from "@/infrastructure/storage/AppSettingsStorageService";
import { HubProfileStorageService } from "@/infrastructure/storage/HubProfileStorageService";
import { LocalStorageService } from "@/infrastructure/storage/LocalStorageService";
import { OrgStorageService } from "@/infrastructure/storage/OrgStorageService";
import { SavedViewsStorageService } from "@/infrastructure/storage/SavedViewsStorageService";
import { SessionStorageService } from "@/infrastructure/storage/SessionStorageService";
import { TokenService } from "@/infrastructure/storage/TokenService";
import { UserStorageService } from "@/infrastructure/storage/UserStorageService";

// ── Core ───────────────────────────────────────────────────────────────────────

let loggerInstance: ILogger;
let monitoringInstance: IMonitoring;

export function getLogger(): ILogger {
  if (!loggerInstance) loggerInstance = logger;
  return loggerInstance;
}

export function getMonitoring(): IMonitoring {
  if (!monitoringInstance)
    monitoringInstance = {
      captureException,
      captureMessage,
      setUserContext,
      clearUserContext,
    };
  return monitoringInstance;
}

// ── Storage ────────────────────────────────────────────────────────────────────

let localStorageServiceInstance: LocalStorageService;
let sessionStorageServiceInstance: SessionStorageService;
let tokenServiceInstance: TokenService;
let userStorageServiceInstance: UserStorageService;
let hubProfileStorageInstance: HubProfileStorageService;
let appSettingsStorageInstance: AppSettingsStorageService;
let orgStorageInstance: OrgStorageService;
let savedViewsStorageInstance: SavedViewsStorageService;

export function getLocalStorageService(): IStorageService {
  if (!localStorageServiceInstance) {
    localStorageServiceInstance = new LocalStorageService();
  }
  return localStorageServiceInstance;
}

export function getSessionStorageService(): IStorageService {
  if (!sessionStorageServiceInstance) {
    sessionStorageServiceInstance = new SessionStorageService();
  }
  return sessionStorageServiceInstance;
}

export function getTokenService(): ITokenService {
  if (!tokenServiceInstance) {
    tokenServiceInstance = new TokenService(getLocalStorageService());
  }
  return tokenServiceInstance;
}

export function getUserStorageService(): IUserStorageService {
  if (!userStorageServiceInstance) {
    userStorageServiceInstance = new UserStorageService(getLocalStorageService());
  }
  return userStorageServiceInstance;
}

export function getHubProfileStorageService(): HubProfileStorageService {
  if (!hubProfileStorageInstance) {
    hubProfileStorageInstance = new HubProfileStorageService(getLocalStorageService());
  }
  return hubProfileStorageInstance;
}

export function getAppSettingsStorageService(): AppSettingsStorageService {
  if (!appSettingsStorageInstance) {
    appSettingsStorageInstance = new AppSettingsStorageService(getLocalStorageService());
  }
  return appSettingsStorageInstance;
}

export function getOrgStorageService(): OrgStorageService {
  if (!orgStorageInstance) {
    orgStorageInstance = new OrgStorageService(getLocalStorageService());
  }
  return orgStorageInstance;
}

export function getSavedViewsStorageService(): SavedViewsStorageService {
  if (!savedViewsStorageInstance) {
    savedViewsStorageInstance = new SavedViewsStorageService(getLocalStorageService());
  }
  return savedViewsStorageInstance;
}

// ── File download ─────────────────────────────────────────────────────────────

let fileDownloadInstance: FileDownloadService;

export function getFileDownloadService(): FileDownloadService {
  if (!fileDownloadInstance) fileDownloadInstance = new FileDownloadService();
  return fileDownloadInstance;
}

// ── Auth services ──────────────────────────────────────────────────────────────

let authServiceInstance: IAuthenticationService;
let tokenManagerInstance: TokenManager;

export function getAuthService(): IAuthenticationService {
  if (!authServiceInstance) {
    authServiceInstance = new AuthenticationService();
  }
  return authServiceInstance;
}

export function getTokenManager(): TokenManager {
  if (!tokenManagerInstance) {
    tokenManagerInstance = new TokenManager(getTokenService(), getAuthService(), getLogger());
  }
  return tokenManagerInstance;
}

// ── Event notifiers ────────────────────────────────────────────────────────────

let sessionExpiredNotifierInstance: EventNotifier<[message?: string]>;
let loginRedirectNotifierInstance: EventNotifier;

export function getSessionExpiredNotifier(): EventNotifier<[message?: string]> {
  if (!sessionExpiredNotifierInstance) {
    sessionExpiredNotifierInstance = new EventNotifier();
  }
  return sessionExpiredNotifierInstance;
}

export function getLoginRedirectNotifier(): EventNotifier {
  if (!loginRedirectNotifierInstance) {
    loginRedirectNotifierInstance = new EventNotifier();
  }
  return loginRedirectNotifierInstance;
}

// ── Navigation ────────────────────────────────────────────────────────────────

let navigationServiceInstance: INavigationService;

export function getNavigationService(): INavigationService {
  if (!navigationServiceInstance) {
    navigationServiceInstance = {
      forceNavigate,
      openExternalUrl,
      reloadPage,
      getPageOrigin,
    };
  }
  return navigationServiceInstance;
}

let projectSpreadsheetServiceInstance: IProjectSpreadsheetService;
export function getProjectSpreadsheetService(): IProjectSpreadsheetService {
  if (!projectSpreadsheetServiceInstance)
    projectSpreadsheetServiceInstance = new ProjectSpreadsheetService();
  return projectSpreadsheetServiceInstance;
}

const folderScanner: IFolderScanner = { filterFolderFileList, scanEntries };
const zipExtractor: IZipExtractor = { extractZip };

export const getFolderScanner = (): IFolderScanner => folderScanner;
export const getZipExtractor = (): IZipExtractor => zipExtractor;

const signedUrlUploader: ISignedUrlUploader = { upload: uploadFileToSignedUrl };

export const getSignedUrlUploader = (): ISignedUrlUploader => signedUrlUploader;
